/** People Landing — editor de bloques. Hasta el 21/09/2026 vivía en BlockEditorDialog.tsx (1.017 líneas). */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { trpc } from '@/lib/trpc';

export const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;
export type AllowedImageType = (typeof ALLOWED_IMAGE_TYPES)[number];

export function ImageField({
  pageSlug,
  imageKey,
  previewUrl,
  onChange,
  compact,
}: {
  pageSlug: string;
  imageKey?: string;
  previewUrl?: string;
  onChange: (key: string | undefined) => void;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const uploadUrlMutation = trpc.peopleLanding.getImageUploadUrl.useMutation();

  const shown = localPreview ?? (imageKey ? previewUrl : undefined);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type as AllowedImageType)) {
      toast.error(t('people_landing.editor.image_type_error', 'Formato no soportado (png/jpg/webp/gif)'));
      return;
    }
    setUploading(true);
    try {
      const { uploadUrl, imageKey: newKey } = await uploadUrlMutation.mutateAsync({
        pageSlug,
        contentType: file.type as AllowedImageType,
      });
      const res = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      // fetch NO lanza en 4xx/5xx: sin este check, un presign vencido "éxita"
      // en silencio y queda un imageKey sin objeto detrás (imagen rota para todos)
      if (!res.ok) throw new Error(`S3 ${res.status}`);
      setLocalPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
      onChange(newKey);
    } catch {
      toast.error(t('people_landing.editor.image_upload_error', 'No se pudo subir la imagen'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      {shown ? (
        <img
          src={shown}
          alt=""
          className={compact ? 'h-14 w-14 rounded-md object-cover border' : 'h-20 w-28 rounded-md object-cover border'}
        />
      ) : (
        <div
          className={`flex items-center justify-center rounded-md border border-dashed text-muted-foreground ${compact ? 'h-14 w-14' : 'h-20 w-28'}`}
        >
          <ImagePlus className="h-5 w-5" />
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_IMAGE_TYPES.join(',')}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <ImagePlus className="mr-1.5 h-3.5 w-3.5" />
          )}
          {!uploading &&
            (imageKey
              ? t('people_landing.editor.change_image', 'Cambiar')
              : t('people_landing.editor.upload_image', 'Subir imagen'))}
        </Button>
        {imageKey && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={() => {
              setLocalPreview(null);
              onChange(undefined);
            }}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
